import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom'
import { Header } from '@/components/common/Header'
import { Footer } from '@/components/common/Footer'
import { GlobalCartButton } from '@/components/cart/GlobalCartButton'
import { Toaster } from 'sonner'
import { CookieConsentModal } from '@/components/common/CookieConsent'
import { TrackOrderBanner } from '@/pages/shop/OrderTracking/components/TrackOrderBanner'
import { useEffect, Suspense, lazy, useState } from 'react'
import { DeliverySelectionModal } from '@/components/delivery/DeliverySelectionModal'
import { useAuthStore, useUIStore } from '@/stores'
import { useAuthUser } from '@/api_communication/hooks/auth/useAuth'
import { useAnalytics } from '@/utils/useAnalytics'
import { useGoogleAnalyticsPageViews } from '@/analytics/useGoogleAnalyticsPageViews'
import { logger } from '@/utils/logger'

const CartDrawer = lazy(() =>
  import('@/components/cart/CartDrawer').then((m) => ({ default: m.CartDrawer }))
)

function App() {
  const setUser = useAuthStore((s) => s.setUser)
  const { activeModal, closeModal, isCartOpen } = useUIStore()
  const [loadCartDrawer, setLoadCartDrawer] = useState(false)
  const { data: user, isSuccess } = useAuthUser()
  const location = useLocation()
  const isRcp = location.pathname.startsWith('/rcp')
  const isManagementPath = [
    '/moderator',
    '/admin',
    '/manage',
    '/kuchnia',
    '/dostawa',
    '/rcp',
    '/pracownicy',
    '/kadry',
    '/pojazdy',
    '/dyspozycyjnosc',
  ].some((path) => location.pathname.startsWith(path))

  useAnalytics()
  useGoogleAnalyticsPageViews()

  useEffect(() => {
    if (isSuccess && user) {
      setUser(user)
      logger.setUser({ id: user.id, email: user.email, role: user.role })
    }
  }, [isSuccess, user, setUser])

  useEffect(() => {
    logger.addBreadcrumb({ category: 'navigation', message: `Route changed: ${location.pathname}` })
  }, [location.pathname])

  useEffect(() => {
    if (isCartOpen) setLoadCartDrawer(true)
  }, [isCartOpen])

  if (isRcp) {
    return (
      <div className="h-dvh overflow-hidden bg-white font-sans text-gray-900">
        <Suspense fallback={
          <div className="flex h-screen items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary" />
          </div>
        }>
          <Outlet />
        </Suspense>
        <Toaster position="top-right" richColors />
        <ScrollRestoration />
      </div>
    )
  }

  const showCart = !isManagementPath

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans text-gray-900">
      <Header />
      <main className="flex-grow">
        <Suspense fallback={
          <div className="min-h-screen bg-white flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-primary rounded-full animate-spin" />
            <p className="mt-4 text-sm text-gray-500 tracking-widest uppercase font-medium">Ładowanie...</p>
          </div>
        }>
          <Outlet />
        </Suspense>
      </main>
      <Footer />

      {showCart && (
        <>
          <GlobalCartButton onPrepareCart={() => setLoadCartDrawer(true)} />
          {loadCartDrawer && (
            <Suspense fallback={null}>
              <CartDrawer />
            </Suspense>
          )}
        </>
      )}
      <TrackOrderBanner />
      <CookieConsentModal />
      <DeliverySelectionModal
        isOpen={activeModal === 'delivery-selection'}
        onClose={closeModal}
      />
      <Toaster position="top-right" richColors />
      <ScrollRestoration />
    </div>
  )
}

export default App
